(() => {
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const number = (value) => (Number.isFinite(Number(value)) ? Number(value) : null);
  const field = (value, name) => {
    const match = String(value || '').match(new RegExp(`(?:^|[,;]\\s*)${name}\\s*:\\s*(-?\\d+(?:\\.\\d+)?)`, 'i'));
    return match ? number(match[1]) : null;
  };
  const exactFromText = (value) => {
    const crop = {
      x: field(value, 'sx'),
      y: field(value, 'sy'),
      width: field(value, 'sw'),
      height: field(value, 'sh'),
    };
    return Object.values(crop).every((entry) => entry !== null)
      && crop.x >= 0 && crop.y >= 0 && crop.width > 0 && crop.height > 0
      && crop.x + crop.width <= 1.001 && crop.y + crop.height <= 1.001
      ? crop
      : null;
  };
  const legacyFromText = (value) => ({
    x: field(value, 'x'),
    y: field(value, 'y'),
    zoom: field(value, 'zoom'),
    normalizedX: field(value, 'nx'),
    normalizedY: field(value, 'ny'),
  });
  const sameLegacy = (left, right) => {
    const sameZoom = left.zoom !== null && right.zoom !== null
      && Math.abs(left.zoom - right.zoom) <= 0.001;
    const sameNormalizedPosition = left.normalizedX !== null && right.normalizedX !== null
      && Math.abs(left.normalizedX - right.normalizedX) <= 0.000001
      && left.normalizedY !== null && right.normalizedY !== null
      && Math.abs(left.normalizedY - right.normalizedY) <= 0.000001;
    const samePixelPosition = left.x !== null && right.x !== null
      && Math.abs(left.x - right.x) <= 0.01
      && left.y !== null && right.y !== null
      && Math.abs(left.y - right.y) <= 0.01;
    return sameZoom && (sameNormalizedPosition || samePixelPosition);
  };
  const parseObjectPosition = (value) => {
    const tokens = String(value || '50% 50%').trim().split(/\s+/);
    const keyword = { left: 0, top: 0, center: 0.5, right: 1, bottom: 1 };
    const parse = (token, fallback) => {
      if (token in keyword) return keyword[token];
      if (/%$/.test(token)) return clamp((Number.parseFloat(token) || 0) / 100, 0, 1);
      return fallback;
    };
    return {
      x: parse(tokens[0], 0.5),
      y: parse(tokens[1] || tokens[0], 0.5),
    };
  };
  const cropForElement = (image, frame = image?.parentElement) => {
    const naturalWidth = image?.naturalWidth || 0;
    const naturalHeight = image?.naturalHeight || 0;
    const frameWidth = frame?.clientWidth || frame?.getBoundingClientRect?.().width || 0;
    const frameHeight = frame?.clientHeight || frame?.getBoundingClientRect?.().height || 0;
    if (!naturalWidth || !naturalHeight || !frameWidth || !frameHeight) return null;

    const style = window.getComputedStyle(image);
    const fit = style.objectFit || 'fill';
    if (fit !== 'cover') return null;
    const fitScale = Math.max(frameWidth / naturalWidth, frameHeight / naturalHeight);
    const renderedWidth = naturalWidth * fitScale;
    const renderedHeight = naturalHeight * fitScale;
    const objectPosition = parseObjectPosition(style.objectPosition);
    const baseX = Math.max(0, renderedWidth - frameWidth) * objectPosition.x / fitScale;
    const baseY = Math.max(0, renderedHeight - frameHeight) * objectPosition.y / fitScale;

    const transform = style.transform;
    const matrix = transform && transform !== 'none' ? new DOMMatrixReadOnly(transform) : null;
    const transformScale = matrix ? Math.max(0.0001, Math.hypot(matrix.a, matrix.b)) : 1;
    const cropWidth = Math.min(naturalWidth, frameWidth / fitScale / transformScale);
    const cropHeight = Math.min(naturalHeight, frameHeight / fitScale / transformScale);
    const baseCenterX = baseX + frameWidth / fitScale / 2;
    const baseCenterY = baseY + frameHeight / fitScale / 2;
    const centerX = baseCenterX - (matrix?.e || 0) / fitScale / transformScale;
    const centerY = baseCenterY - (matrix?.f || 0) / fitScale / transformScale;
    const sourceX = clamp(centerX - cropWidth / 2, 0, Math.max(0, naturalWidth - cropWidth));
    const sourceY = clamp(centerY - cropHeight / 2, 0, Math.max(0, naturalHeight - cropHeight));
    return {
      x: sourceX / naturalWidth,
      y: sourceY / naturalHeight,
      width: cropWidth / naturalWidth,
      height: cropHeight / naturalHeight,
    };
  };
  const cropSuffix = (crop) => crop
    ? `, sx:${crop.x.toFixed(8)}, sy:${crop.y.toFixed(8)}, sw:${crop.width.toFixed(8)}, sh:${crop.height.toFixed(8)}`
    : '';
  const merge = (existing, legacy, image, frame) => {
    const saved = exactFromText(existing);
    const crop = saved && sameLegacy(legacyFromText(existing), legacyFromText(legacy))
      ? saved
      : cropForElement(image, frame);
    return `${legacy}${cropSuffix(crop)}`;
  };
  const mergeJson = (existing, nextState, image, frame) => {
    let previous = {};
    try { previous = JSON.parse(String(existing || '{}')); } catch { previous = {}; }
    const unchanged = number(previous.scale) !== null
      && Math.abs(Number(previous.scale) - Number(nextState.scale)) <= 0.001
      && Math.abs(Number(previous.x) - Number(nextState.x)) <= 0.0001
      && Math.abs(Number(previous.y) - Number(nextState.y)) <= 0.0001;
    const saved = previous.crop
      && [previous.crop.x, previous.crop.y, previous.crop.width, previous.crop.height].every((entry) => number(entry) !== null)
      ? previous.crop
      : null;
    return JSON.stringify({
      ...nextState,
      crop: unchanged && saved ? saved : cropForElement(image, frame),
    });
  };
  const withoutExactText = (value) => String(value || '')
    .replace(/,\s*s(?:x|y|w|h)\s*:\s*-?\d+(?:\.\d+)?/gi, '');
  const refreshTextInput = (input, image) => {
    if (!input || !image) return;
    input.value = merge(input.value, withoutExactText(input.value), image, image.parentElement);
  };
  const syncForm = (form) => {
    form?.querySelectorAll?.('[data-renao-collage-position]').forEach((input) => {
      const slot = input.dataset.renaoCollagePosition;
      refreshTextInput(input, document.querySelector(`[data-renao-collage-preview-image="${slot}"]`));
    });
    form?.querySelectorAll?.('[data-renao-netflix-position]').forEach((input) => {
      const slot = input.dataset.renaoNetflixPosition;
      refreshTextInput(input, document.querySelector(`[data-renao-netflix-image-slot="${slot}"]`));
    });
    [
      ['[data-renao-music-photo-position]', '[data-renao-music-photo-preview]'],
      ['[data-renao-qr-photo-position]', '[data-renao-qr-photo-preview]'],
      ['[data-renao-heart-photo-position]', '[data-renao-heart-photo-preview]'],
    ].forEach(([inputSelector, imageSelector]) => {
      refreshTextInput(form?.querySelector?.(inputSelector), document.querySelector(imageSelector));
    });
    const iyikiInput = form?.querySelector?.('[data-renao-iyiki-photo-position]');
    const iyikiImage = document.querySelector('[data-renao-iyiki-photo-preview]');
    if (iyikiInput && iyikiImage) {
      try {
        const parsed = JSON.parse(iyikiInput.value || '{}');
        iyikiInput.value = mergeJson(iyikiInput.value, {
          scale: number(parsed.scale) ?? 1,
          x: number(parsed.x) ?? 0,
          y: number(parsed.y) ?? 0,
        }, iyikiImage, iyikiImage.parentElement);
      } catch {
        // The personalizer will surface invalid legacy state; do not block submit here.
      }
    }
  };

  const IDENTITY_TEXT = 'x:0, y:0, zoom:1.00, nx:0.000000, ny:0.000000, sx:0.00000000, sy:0.00000000, sw:1.00000000, sh:1.00000000';
  const fileForInput = (input) => {
    const pending = input?.__renaoPendingFile;
    if (pending instanceof File && pending.size > 0) return pending;
    const selected = input?.files?.[0];
    return selected instanceof File && selected.size > 0 ? selected : null;
  };
  const decodeFile = (file) => new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => resolve({ image, url });
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not prepare the photo crop for printing. Select your photo again.'));
    };
    image.src = url;
  });
  const waitForPreviewImage = (image) => new Promise((resolve, reject) => {
    if (!image) {
      reject(new Error('Photo preview not found. Select your photo again.'));
      return;
    }
    if (image.complete && image.naturalWidth > 0 && image.naturalHeight > 0) {
      resolve();
      return;
    }
    let finished = false;
    const finish = (error = null) => {
      if (finished) return;
      finished = true;
      window.clearTimeout(timer);
      image.removeEventListener('load', onLoad);
      image.removeEventListener('error', onError);
      if (error) reject(error);
      else resolve();
    };
    const onLoad = () => {
      if (image.naturalWidth > 0 && image.naturalHeight > 0) finish();
      else finish(new Error('Could not read the photo dimensions. Select your photo again.'));
    };
    const onError = () => finish(new Error('Could not open the photo preview. Select your photo again.'));
    const timer = window.setTimeout(() => {
      finish(new Error('Could not prepare the photo preview. Please try again.'));
    }, 12000);
    image.addEventListener('load', onLoad, { once: true });
    image.addEventListener('error', onError, { once: true });
    if (image.complete) onLoad();
  });
  const canvasBlob = (canvas, type, quality) => new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('Could not prepare the photo crop for printing. Please try again.'));
    }, type, quality);
  });
  const frozenName = (name, type) => {
    const base = String(name || 'fotograf').replace(/\.[^.]+$/, '').replace(/-baski$/i, '');
    return `${base}-baski.${type === 'image/png' ? 'png' : 'jpg'}`;
  };
  const freezeEntry = async ({ input, position, image, json = false }) => {
    if (!input || !position || !image) return;
    const crop = json
      ? (() => {
          try {
            const parsed = JSON.parse(position.value || '{}');
            const saved = parsed.crop;
            return saved && [saved.x, saved.y, saved.width, saved.height].every((entry) => number(entry) !== null)
              ? saved
              : null;
          } catch { return null; }
        })()
      : exactFromText(position.value);
    const selectedFile = fileForInput(input);
    if (!selectedFile) return;
    if (!crop) {
      const slot = position.dataset.renaoCollagePosition
        || position.dataset.renaoNetflixPosition
        || '';
      const label = slot ? ` ${slot}` : '';
      throw new Error(`Could not verify the print crop for photo${label}. Please try again.`);
    }

    const sourceFile = input.__renaoCropSourceFile instanceof File
      ? input.__renaoCropSourceFile
      : selectedFile;
    if (!(input.__renaoCropSourceFile instanceof File)) input.__renaoCropSourceFile = sourceFile;
    const signature = [crop.x, crop.y, crop.width, crop.height]
      .map((entry) => Number(entry).toFixed(8)).join(':');
    if (input.__renaoFrozenCropSignature === signature && input.__renaoPendingFile instanceof File) {
      position.value = json
        ? JSON.stringify({ scale: 1, x: 0, y: 0, crop: { x: 0, y: 0, width: 1, height: 1 } })
        : IDENTITY_TEXT;
      return;
    }

    const decoded = await decodeFile(sourceFile);
    try {
      const width = decoded.image.naturalWidth;
      const height = decoded.image.naturalHeight;
      if (!width || !height) throw new Error('Could not read the photo dimensions. Select your photo again.');
      const outputWidth = Math.max(1, Math.round(Number(crop.width) * width));
      const outputHeight = Math.max(1, Math.round(Number(crop.height) * height));
      const canvas = document.createElement('canvas');
      canvas.width = outputWidth;
      canvas.height = outputHeight;
      const context = canvas.getContext('2d', { alpha: true });
      if (!context) throw new Error('Could not prepare the photo crop for printing. Please try again.');
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = 'high';
      context.drawImage(
        decoded.image,
        Number(crop.x) * width,
        Number(crop.y) * height,
        Number(crop.width) * width,
        Number(crop.height) * height,
        0,
        0,
        outputWidth,
        outputHeight
      );
      const outputType = sourceFile.type === 'image/png' ? 'image/png' : 'image/jpeg';
      const blob = await canvasBlob(canvas, outputType, 0.96);
      const frozenFile = new File([blob], frozenName(sourceFile.name, outputType), {
        type: outputType,
        lastModified: Date.now(),
      });
      input.__renaoPendingFile = frozenFile;
      input.__renaoFrozenCropSignature = signature;
      position.value = json
        ? JSON.stringify({ scale: 1, x: 0, y: 0, crop: { x: 0, y: 0, width: 1, height: 1 } })
        : IDENTITY_TEXT;
    } finally {
      URL.revokeObjectURL(decoded.url);
    }
  };
  const freezeForm = async (form) => {
    if (!form) return;
    const entries = [];
    form.querySelectorAll('[data-renao-collage-position]').forEach((position) => {
      const slot = position.dataset.renaoCollagePosition;
      entries.push({
        position,
        input: form.querySelector(`[data-renao-collage-input="${slot}"]`),
        image: document.querySelector(`[data-renao-collage-preview-image="${slot}"]`),
      });
    });
    form.querySelectorAll('[data-renao-netflix-position]').forEach((position) => {
      const slot = position.dataset.renaoNetflixPosition;
      entries.push({
        position,
        input: form.querySelector(`[data-renao-netflix-photo-input][data-renao-netflix-slot="${slot}"]`),
        image: document.querySelector(`[data-renao-netflix-image-slot="${slot}"]`),
      });
    });
    [
      ['[data-renao-music-photo-input]', '[data-renao-music-photo-position]', '[data-renao-music-photo-preview]'],
      ['[data-renao-qr-photo-input]', '[data-renao-qr-photo-position]', '[data-renao-qr-photo-preview]'],
      ['[data-renao-heart-photo-input]', '[data-renao-heart-photo-position]', '[data-renao-heart-photo-preview]'],
    ].forEach(([inputSelector, positionSelector, imageSelector]) => entries.push({
      input: form.querySelector(inputSelector),
      position: form.querySelector(positionSelector),
      image: document.querySelector(imageSelector),
    }));
    entries.push({
      input: form.querySelector('[data-renao-iyiki-photo-input]'),
      position: form.querySelector('[data-renao-iyiki-photo-position]'),
      image: document.querySelector('[data-renao-iyiki-photo-preview]'),
      json: true,
    });
    await Promise.all(entries.map(async (entry) => {
      if (!fileForInput(entry.input)) return;
      await waitForPreviewImage(entry.image);
    }));
    // The final slot is commonly the last image Chromium decodes after a bulk
    // upload. Measure crops only after every selected preview is ready; this
    // prevents a quick submit from silently uploading one uncropped source.
    syncForm(form);
    for (const entry of entries) await freezeEntry(entry);
  };

  const prepareForm = (form) => {
    if (!form) return Promise.resolve();
      if (form.querySelector('[name="properties[_Renao Üretim]"][value="mac-pdf-v1"]')) {
    syncForm(form);
    return Promise.resolve();
  }
    const previous = form.__renaoCropPreparationPromise;
    const run = () => freezeForm(form);
    const next = previous
      ? Promise.resolve(previous).catch(() => {}).then(run)
      : run();
    form.__renaoCropPreparationPromise = next;
    return next;
  };

  globalThis.RenaoCropRecipe = {
    cropForElement,
    exactFromText,
    merge,
    mergeJson,
    syncForm,
    freezeForm,
    prepareForm,
  };
})();
